import React, { useState, useRef, useEffect } from 'react';
import ReactPlayer from 'react-player';

// Helpers
import { getTrailerUrl } from '../../utils/helpers';
import { isMobile } from '../../utils/isMobile';

import { IAssetTrailer } from '../../utils/models';

import TrailerControls from '../../components/TrailerControls';

interface VideoDocument extends Document {
  webkitCurrentFullScreenElement: any;
  msFullscreenElement: any;
}

const useTrailer = (
  assetTrailer: IAssetTrailer | null,
  hidden: boolean,
  label?: string
) => {
  // Controls
  const [isPlayingTrailer, setIsPlayingTrailer] = useState<boolean>(false);
  const [isTrailerMuted, setIsTrailerMuted] = useState<boolean>(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const fullScreenButtonRef = useRef<HTMLButtonElement | null>(null);

  // Player
  const [hasStartedPlayingTrailer, setHasStartedPlayingTrailer] =
    useState<boolean>(false);

  const [videoPlayerMounted, setVideoPlayerMounted] = useState<boolean>(false);
  const videoPlayerRef = useRef<ReactPlayer | null>(null);

  const trailerUrl = getTrailerUrl(assetTrailer);

  // Listeners for native video player events to keep trailer controls in sync

  useEffect(() => {
    const fullScreenElement =
      document.fullscreenElement ||
      (document as VideoDocument).webkitCurrentFullScreenElement ||
      (document as VideoDocument).msFullscreenElement;

    const handleVolumeChange = (e) => {
      if (fullScreenElement) {
        setIsTrailerMuted(e.target.volume === 0 || e.target.muted);
      }
    };

    const handlePauseChange = () => {
      if (fullScreenElement) {
        setIsPlayingTrailer(false);
      }
    };

    const handlePlayChange = () => {
      if (fullScreenElement) {
        setIsPlayingTrailer(true);
      }
    };

    const handleFullScreenChange = () => {
      if (!fullScreenElement) {
        fullScreenButtonRef?.current?.focus();
      }
    };

    if (videoPlayerRef.current) {
      const videoElement: any = videoPlayerRef?.current?.getInternalPlayer();

      // Set the captions track to be hidden by default. Can be toggled on in native player by the user
      if (videoElement?.textTracks.length) {
        const [textTrack] = videoElement.textTracks;
        textTrack.mode = 'hidden';
      }

      if (videoElement) {
        videoElement.addEventListener('play', handlePlayChange);
        videoElement.addEventListener('pause', handlePauseChange);
        videoElement.addEventListener('volumechange', handleVolumeChange);
        videoElement.addEventListener(
          'fullscreenchange',
          handleFullScreenChange
        );
      }

      return () => {
        if (videoElement) {
          videoElement.removeEventListener('play', handlePlayChange);
          videoElement.removeEventListener('pause', handlePauseChange);
          videoElement.removeEventListener('volumechange', handleVolumeChange);
          videoElement.removeEventListener(
            'fullscreenchange',
            handleFullScreenChange
          );
        }
      };
    }
  }, [
    videoPlayerRef,
    videoPlayerMounted,
    setIsTrailerMuted,
    setIsPlayingTrailer,
    assetTrailer,
  ]);

  // Reset video player state
  const onTrailerEnd = () => {
    setIsPlayingTrailer(false);
    setHasStartedPlayingTrailer(false);
    setVideoPlayerMounted(false);
  };

  useEffect(() => {
    if (
      !document.fullscreenElement ||
      !(document as VideoDocument).webkitCurrentFullScreenElement ||
      !(document as VideoDocument).msFullscreenElement
    ) {
      setIsFullScreen(false);
    }
  }, [isPlayingTrailer, isFullScreen, setIsFullScreen]);

  const toggleFullScreen = () => {
    const videoEl: any = videoPlayerRef?.current?.getInternalPlayer();
    if (!videoEl) {
      return;
    }

    if (
      !document.fullscreenElement ||
      !(document as VideoDocument).webkitCurrentFullScreenElement ||
      !(document as VideoDocument).msFullscreenElement
    ) {
      if (videoEl.requestFullscreen) {
        videoEl.requestFullscreen();
        setIsFullScreen(true);
      } else if (videoEl.mozRequestFullScreen) {
        videoEl.mozRequestFullScreen();
        setIsFullScreen(true);
      } else if (videoEl.webkitRequestFullScreen) {
        videoEl.webkitRequestFullScreen();
        setIsFullScreen(true);
      } else if (videoEl.msRequestFullscreen) {
        videoEl.msRequestFullscreen();
        setIsFullScreen(true);
      }
    }
  };

  // Triggers native video player by default on iOS devices
  useEffect(() => {
    if (isMobile.iOSDevice() && isPlayingTrailer) {
      toggleFullScreen();
    }
  }, [isPlayingTrailer]);

  // Stops the trailer from playing when the carousel item is moved off screen
  useEffect(() => {
    if (hidden) {
      setIsPlayingTrailer(false);
      setHasStartedPlayingTrailer(false);
    }
  }, [hidden, setIsPlayingTrailer, setHasStartedPlayingTrailer]);

  const videoPlayer = trailerUrl ? (
    <ReactPlayer
      className="video-player"
      width="100%"
      height="100%"
      url={trailerUrl}
      playing={isPlayingTrailer}
      loop={false}
      volume={1}
      muted={isTrailerMuted}
      ref={videoPlayerRef}
      onReady={() => setVideoPlayerMounted(true)}
      onPlay={() => setIsPlayingTrailer(true)}
      onPause={() => setIsPlayingTrailer(false)}
      onEnded={onTrailerEnd}
      controls={
        (videoPlayerRef?.current?.getInternalPlayer() as Document)?.fullscreen
      }
      playsinline={!isMobile.iOSDevice()}
      config={{
        file: {
          // forceDASH: true,
          dashVersion: '2.9.2',
        },
      }}
    />
  ) : null;

  const trailerControls = trailerUrl ? (
    <TrailerControls
      hasStartedPlayingTrailer={hasStartedPlayingTrailer}
      isPlayingTrailer={isPlayingTrailer}
      isTrailerMuted={isTrailerMuted}
      onTogglePlay={() => {
        setIsPlayingTrailer((prevState) => !prevState);
        setHasStartedPlayingTrailer(true);
      }}
      onToggleMute={() => setIsTrailerMuted((prevState) => !prevState)}
      onToggleFullScreen={toggleFullScreen}
      ref={fullScreenButtonRef}
      hidden={hidden}
      label={label}
    />
  ) : null;

  return {
    hasStartedPlayingTrailer,
    isPlayingTrailer,
    isFullScreen,
    videoPlayer,
    trailerControls,
  };
};

export default useTrailer;
